"use client";

import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGameState } from "@/components/game-state-context";
import { Button } from "@/components/ui/button";
import { useEntityRoster } from "@/hooks/use-entity-roster";
import { cn } from "@/lib/utils";
import { callMcpTool } from "@/src/api/mcp";

/**
 * Pure predicate for auto-centering on turn change.
 * See spec §M7 / §N-r3-1.
 */
export function shouldAutoCenter(args: {
  userInteracted: boolean;
  activeChanged: boolean;
}): boolean {
  return !args.userInteracted && args.activeChanged;
}

export function InitiativeHeader() {
  const { encounter, campaignId, invalidate, loading } = useGameState();
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeChipRef = useRef<HTMLSpanElement>(null);
  const prevActiveSlot = useRef<string | null>(null);
  const prevPassResetCombatant = useRef<string | null>(null);
  const userInteractedWithHeader = useRef(false);
  const [isPassingTurn, setIsPassingTurn] = useState(false);
  const [passTurnError, setPassTurnError] = useState<string | null>(null);

  // Called unconditionally, before the encounterActive early return, to
  // satisfy the rules of hooks — the hook's own null-key guard makes an
  // empty queue a no-op fetch.
  const { roster } = useEntityRoster(
    campaignId,
    encounter?.queue.map((s) => s.actorId) ?? []
  );

  const onInteract = () => {
    userInteractedWithHeader.current = true;
  };

  const activeCombatantId = encounter?.activeCombatantId ?? null;
  const activeTurnIndex = encounter?.turnIndex ?? -1;

  const activeMeta =
    activeCombatantId === null
      ? null
      : (roster?.entries[activeCombatantId] ?? null);
  const canPassTurn = activeMeta?.controller === "pc";
  const isEncounterRefreshing = loading.has("encounter");

  const handlePassTurn = useCallback(async () => {
    if (!activeCombatantId) {
      return;
    }

    setIsPassingTurn(true);
    setPassTurnError(null);
    try {
      await callMcpTool(
        "next_turn",
        { actorId: activeCombatantId },
        campaignId
      );
      invalidate(["encounter"]);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to pass turn";
      setPassTurnError(message);
    } finally {
      setIsPassingTurn(false);
    }
  }, [activeCombatantId, campaignId, invalidate]);

  useEffect(() => {
    if (prevPassResetCombatant.current === activeCombatantId) {
      return;
    }
    prevPassResetCombatant.current = activeCombatantId;
    setPassTurnError(null);
    setIsPassingTurn(false);
  }, [activeCombatantId]);

  useEffect(() => {
    const activeSlot =
      activeCombatantId === null
        ? null
        : `${activeTurnIndex}:${activeCombatantId}`;
    const activeChanged = activeSlot !== prevActiveSlot.current;
    prevActiveSlot.current = activeSlot;

    if (
      shouldAutoCenter({
        userInteracted: userInteractedWithHeader.current,
        activeChanged,
      })
    ) {
      activeChipRef.current?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
    userInteractedWithHeader.current = false;
  }, [activeCombatantId, activeTurnIndex]);

  if (!encounter?.encounterActive) {
    return null;
  }

  const meta = (id: string) =>
    roster?.entries[id] ?? {
      displayName: id,
      controller: "npc" as const,
      defeated: false,
    };

  return (
    <div className="border-border border-b bg-[#1a1a22] px-3 py-2">
      <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
        Initiative · Round {encounter.round}
      </div>
      <div
        className="flex gap-1 overflow-x-auto pb-0.5 [mask-image:linear-gradient(to_right,transparent,black_20px,black_calc(100%_-_20px),transparent)]"
        onPointerDown={onInteract}
        onTouchStart={onInteract}
        onWheel={onInteract}
        ref={scrollRef}
      >
        {encounter.queue.map((slot, index) => {
          const m = meta(slot.actorId);
          const isActive =
            encounter.activeCombatantId !== null &&
            index === encounter.turnIndex;
          const isPending = slot.initiative === null;
          const slotKey = slot.extraTurn
            ? `${slot.actorId}:${slot.extraTurn.id}`
            : `${slot.actorId}:base`;
          const dotClass =
            m.controller === "pc"
              ? "bg-blue-400"
              : m.controller === "ally"
                ? "bg-green-500"
                : "bg-red-500";

          return (
            <span
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs",
                isActive
                  ? "border-green-500/70 bg-green-800/20 font-semibold text-green-200"
                  : "border-border bg-card text-muted-foreground",
                m.defeated && "line-through opacity-35"
              )}
              key={slotKey}
              ref={isActive ? activeChipRef : undefined}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", dotClass)} />
              <span className="max-w-[12ch] truncate">{m.displayName}</span>
              <span className="font-mono text-[10px] tabular-nums opacity-70">
                {isPending ? "rolling…" : slot.initiative}
              </span>
            </span>
          );
        })}
      </div>
      {canPassTurn ? (
        <div className="mt-2 space-y-1.5">
          <Button
            className="h-10 w-full"
            disabled={isPassingTurn || isEncounterRefreshing}
            onClick={handlePassTurn}
            size="sm"
            type="button"
          >
            {isPassingTurn ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Pass Turn
          </Button>
          {passTurnError ? (
            <p className="break-words text-destructive text-xs">
              {passTurnError}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
